import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "@app/shared/src/api/api.gen";
import { AcceptedTerms, IAcceptedTerms } from "@app/shared/src/models";
import { Btn, useToast } from "@app/shared/src/components_v2";
import { LATEST_TERMS_FILE, LATEST_TERMS_PATH, latestTermsAcceptance } from "../../helpers/terms";
import { AuthFrame } from "../layouts/AuthFrame";
import { useSession } from "../providers/SessionProvider";

/**
 * The Terms and Conditions, readable by anyone and acceptable by whoever is signed in.
 *
 * Reachable from the sign-in page and from every footer, so it works signed out — a prospective
 * customer reads it before creating an account. Signed in, it also shows when this revision was
 * accepted, or offers to record that; the record is per file name and shared with the main
 * dashboard, so accepting in one place counts in both.
 */
export const TermsPage: React.FC = () => {
    const { user, refresh } = useSession();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const accepted = latestTermsAcceptance(user);

    const accept = async () => {
        if (!user?.id || busy) return;
        setBusy(true);
        setError(undefined);
        try {
            await Users.acceptTerms(user.id, new AcceptedTerms({ id: undefined, fileName: LATEST_TERMS_FILE, acceptedAt: undefined } as IAcceptedTerms));
            await refresh();
            toast("Terms accepted");
        } catch (err) {
            setError(err instanceof Error && err.message ? err.message : "Could not record your acceptance");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthFrame wide>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-[22px] font-bold tracking-tight">Terms and Conditions</h1>
                    <p className="mt-1 text-[12.5px] text-[var(--muted)]">
                        Omnisight services, including Fleet Commander.{" "}
                        <a href={LATEST_TERMS_PATH} target="_blank" rel="noreferrer" className="font-semibold text-[var(--accent)]">
                            Open the PDF in a new tab
                        </a>
                    </p>
                </div>
                <Link to={user ? "/overview" : "/login"} className="text-[12.5px] font-semibold text-[var(--muted)] hover:text-[var(--text)]">
                    {user ? "← Back to the console" : "← Back to sign in"}
                </Link>
            </div>

            <div className="overflow-hidden rounded-lg border border-[var(--line)] bg-[var(--surface)]">
                <iframe title="Terms and Conditions" src={LATEST_TERMS_PATH} className="block h-[70vh] w-full bg-white" />
            </div>

            {user && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-5 py-4 text-[13px]">
                    {accepted ? (
                        <span className="text-[var(--muted)]">
                            You accepted these terms on{" "}
                            <strong className="text-[var(--text)]">{accepted.acceptedAt ? new Date(accepted.acceptedAt).toLocaleDateString() : "record"}</strong>.
                        </span>
                    ) : (
                        <>
                            <span className="text-[var(--muted)]">Signed in as {user.email}. Read the terms, then record that you accept them.</span>
                            <Btn onClick={accept} disabled={busy}>
                                {busy ? "Recording…" : "Accept the terms"}
                            </Btn>
                        </>
                    )}
                    {error && <p className="w-full text-[12.5px] text-[var(--crit)]">{error}</p>}
                </div>
            )}
        </AuthFrame>
    );
};
